import React, { useEffect, useState, useMemo } from 'react';
import DatePicker from 'antd/lib/date-picker';
import { MapPin, Calendar, Maximize, X } from 'lucide-react';
import moment from 'moment';
import '../DistributionChart/DistributionChart.css';
import { FilterParams } from '../leadsGrid/leads.types';
import { distributionChartService } from '../../../services/distributionChartService';
import './LocationMapChart.css';

const { RangePicker } = DatePicker;

interface LocationPoint {
  _id: string;
  name?: string;
  count: number;
  latitude: number;
  longitude: number;
  address?: string;
}

interface LocationMapChartProps {
  field: string;
  filterParams: FilterParams;
  onClose?: () => void;
}

const LocationMapChart: React.FC<LocationMapChartProps> = ({ 
  field, 
  filterParams,
  onClose 
}) => {
  // State
  const [locations, setLocations] = useState<LocationPoint[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedLocation, setSelectedLocation] = useState<LocationPoint | null>(null);
  const [selectedDateRange, setSelectedDateRange] = useState(() => {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - 30); // Default to last 30 days for locations
    return {
      startDate: start,
      endDate: end
    };
  });
  
  // Stats calculation
  const stats = useMemo(() => {
    if (!locations.length) return { totalLocations: 0, totalLeads: 0 };
    
    const totalLeads = locations.reduce((sum, location) => sum + location.count, 0);
    return {
      totalLocations: locations.length,
      totalLeads
    };
  }, [locations]);
  
  // Fetch location data
  useEffect(() => {
    const fetchLocationData = async () => {
     
      
      setIsLoading(true);
      setError(null);
      
      try {
        // Prepare request with proper date range
        const finalFilterParams = {
          ...filterParams,
          date: selectedDateRange.startDate && selectedDateRange.endDate ? {
            startedAt: selectedDateRange.startDate.toISOString(),
            endedAt: selectedDateRange.endDate.toISOString()
          } : undefined
        };
        
      
        
        const response = await distributionChartService.fetchLocationData(
          finalFilterParams,
          field
        );
        
        if (Array.isArray(response.data)) {
         
          
          if (response.data.length === 0) {
            setError('No location data available with valid coordinates');
          }
          
          setLocations(response.data);
        } else {
          
          setError('Invalid data format received from server');
        }
      } catch (error) {
       
        setError(error instanceof Error ? error.message : 'Failed to fetch location data');
      } finally {
        setIsLoading(false);
      
      }
    };
    
    fetchLocationData();
  }, [field, filterParams, selectedDateRange]);
  
  // Map initialization and rendering
  useEffect(() => {
    if (locations.length === 0 || isLoading) return;
    
   
    
    // We'll use a simple approach with embedded Google Maps for now
    const mapContainer = document.getElementById('location-map-container');
    if (!mapContainer) {
    
      return;
    }
    
    // Clear any existing map
    mapContainer.innerHTML = '';
    
    if (locations.length === 0) {
      const noDataDiv = document.createElement('div');
      noDataDiv.className = 'no-data-state';
      noDataDiv.textContent = 'No location data with valid coordinates available';
      mapContainer.appendChild(noDataDiv);
   
      return;
    }
    
    // Create map iframe
    const iframe = document.createElement('iframe');
    iframe.style.width = '100%';
    iframe.style.height = '100%';
    iframe.style.position = 'absolute';
    iframe.style.top = '0';
    iframe.style.left = '0';
    iframe.style.border = '0';
    iframe.allowFullscreen = true;
    
    // Generate map URL with markers
    let mapUrl = 'https://maps.google.com/maps?';
    
    // Add markers for each location
    if (locations.length === 1) {
      // Single location - center on it
      const location = locations[0];
      mapUrl += `q=${location.latitude},${location.longitude}`;
      
      if (location.name || location.address) {
        mapUrl += `+(${encodeURIComponent(location.name || location.address || 'Location')})`;
      }
    } else {
      // Multiple locations - add markers
      // Center the map on the average location
      const centerLat = locations.reduce((sum, loc) => sum + loc.latitude, 0) / locations.length;
      const centerLng = locations.reduce((sum, loc) => sum + loc.longitude, 0) / locations.length;
      
      mapUrl += `center=${centerLat},${centerLng}`;
      
      // Add individual markers using the ll parameter (limited to 10 due to URL length constraints)
      const markersParam = locations.slice(0, 10).map(loc => {
        const label = encodeURIComponent((loc.name || loc.address || 'Location').substring(0, 10));
        return `markers=color:red|label:${label}|${loc.latitude},${loc.longitude}`;
      }).join('&');
      
      mapUrl += `&${markersParam}`;
    }
    
    // Set appropriate zoom level
    mapUrl += `&z=${locations.length === 1 ? 14 : 10}&output=embed`;
    
    iframe.src = mapUrl;
    
    // Append to container
    mapContainer.appendChild(iframe);

  }, [locations, isLoading]);
  
  const handleDateRangeChange = (dates: any, dateStrings: [string, string]) => {
    
    
    if (!dates || !dates[0] || !dates[1]) {
      // Reset to default 30-day range if dates are cleared
      const end = new Date();
      const start = new Date();
      start.setDate(end.getDate() - 30);
      
      setSelectedDateRange({
        startDate: start,
        endDate: end
      });
      return;
    }
    
    setSelectedDateRange({
      startDate: dates[0].toDate(),
      endDate: dates[1].toDate()
    });
  };
  
  // Render loading state
  if (isLoading) {
    return (
      <div className="location-map-wrapper">
        <div className="location-map-header">
          <h3 className="location-map-title">Location Distribution</h3>
          {onClose && (
            <button className="location-map-action-btn" onClick={onClose}>
              <X size={20} />
            </button>
          )}
        </div>
        <div className="loading-state">Loading location data...</div>
      </div>
    );
  }
  
  // Render error state
  if (error) {
    return (
      <div className="location-map-wrapper">
        <div className="location-map-header">
          <h3 className="location-map-title">Location Distribution</h3>
          {onClose && (
            <button className="location-map-action-btn" onClick={onClose}>
              <X size={20} />
            </button>
          )}
        </div>
        <div className="error-state">Error: {error}</div>
      </div>
    );
  }
  
  return (
    <div className="location-map-wrapper">
      <div className="location-map-header">
        <div className="location-map-title">
          Location Distribution
        </div>
        
        <div className="location-map-header-metrics">
          <div className="location-map-header-metrics-item">
            <span className="location-map-header-metrics-label">Total Locations:</span>
            <span className="location-map-header-metrics-value">{stats.totalLocations}</span>
          </div>
          <div className="location-map-header-metrics-item">
            <span className="location-map-header-metrics-label">Total Leads:</span>
            <span className="location-map-header-metrics-value">{stats.totalLeads}</span>
          </div>
        </div>
        
        <div className="location-map-date-range">
          <RangePicker
            onChange={handleDateRangeChange}
            value={[
              selectedDateRange.startDate ? moment(selectedDateRange.startDate) : null,
              selectedDateRange.endDate ? moment(selectedDateRange.endDate) : null
            ]}
            placeholder={['Start Date', 'End Date']}
            allowClear={false}
            ranges={{
              'Today': [moment(), moment()],
              'Last 7 Days': [moment().subtract(6, 'days'), moment()],
              'Last 30 Days': [moment().subtract(29, 'days'), moment()],
              'This Month': [moment().startOf('month'), moment()],
              'Last 3 Months': [moment().subtract(3, 'months'), moment()]
            }}
            style={{ minWidth: '300px' }}
          />
        </div>
        
        <div className="location-map-actions">
          <button 
            className="location-map-action-btn"
            title="Fullscreen"
          >
            <Maximize size={16} />
          </button>
          {onClose && (
            <button 
              className="location-map-action-btn"
              onClick={onClose}
              title="Close"
            >
              <X size={16} />
            </button>
          )}
        </div>
      </div>
      
      <div className="location-map-content">
        <div id="location-map-container" className="location-map">
          {locations.length === 0 && !isLoading && (
            <div className="no-data-state">No location data with valid coordinates available</div>
          )}
        </div>
        
        {selectedLocation && (
          <div className="location-details-panel">
            <h4>{selectedLocation.name || 'Location Details'}</h4>
            <p>Leads: {selectedLocation.count}</p>
            {selectedLocation.address && <p>Address: {selectedLocation.address}</p>}
            <p>Coordinates: {selectedLocation.latitude.toFixed(6)}, {selectedLocation.longitude.toFixed(6)}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default LocationMapChart;